import React, { useEffect, useRef, useState } from 'react'
import AceEditor from 'react-ace'
import Text from '../../../../../elements/Typo/Text'
import Icon from '../../../../../components/icons'
import Button from '../../../../../elements/button'
import SaveFeaturesModal from '../modals/saveFeatures.modal'

import 'ace-builds/src-noconflict/mode-mysql'
import 'ace-builds/src-noconflict/theme-xcode'
import 'ace-builds/src-noconflict/ext-language_tools'
import { useExperimentSelector } from '../../../../../store/selectors/experimentSelector'
import ZoneDesignTablesAccordians from '../zoneDesignTablesAccordians'
import { useParams } from 'react-router-dom'
import { useExperimentApi } from '../../../../../store/api/hooks/experimentApiHook'
import { useFpLoader } from '../../../../../store/api/hooks/fpLoaderHook'
import { useDialog } from '../../../../../store/api/hooks/dialogHook'
import toast from 'react-hot-toast'
import { trackEvent } from '../../../../../_common/functions'
import { useTranslation } from 'react-i18next'

interface Props {
    _ref?: any
    applySQL: (data: any) => void
    onadditionalCsvUploaded?: () => void
    currentQueryExecuted: boolean
    setCurrentQueryExecuted: (t: boolean) => void
    type?: 'measurement' | 'design'
    dataType?: 'Measurement' | 'Intervention' | 'Recommendation' | 'recurring'
    selectedDataTable?: string
}

const Sql: React.FC<Props> = ({ _ref, applySQL, currentQueryExecuted, setCurrentQueryExecuted, onadditionalCsvUploaded = () => {}, type = 'design', selectedDataTable, dataType = 'Measurement' }) => {
    const { t } = useTranslation()
    const savefeaturesModalRef: any = useRef()
    const experiment = useExperimentSelector()
    const [query, setQuery] = useState<string>('')
    const [defaultQuery, setDefaultQuery] = useState<string>('')
    const [savedQuery, setSavedQuery] = useState<string[]>([])
    const [additionalFileUploaded, setAdditionalFileUploaded] = useState<boolean>(false)
    //const [progress, setProgress] = useState(0)
    const { showLoader, hideLoader } = useFpLoader()
    const experimentApi = useExperimentApi()
    const dialog = useDialog()
    const params = useParams()

    useEffect(() => {
        if (experiment) {
            if (selectedDataTable) {
                setDefaultQuery('Select * from ' + selectedDataTable)
            } else if (type === 'measurement') {
                switch (dataType) {
                    case 'Measurement':
                        setDefaultQuery('Select * from ' + (experiment.data_mappings?.delta_measurement_table_name ?? experiment.data_mappings?.user_created_measurement_table_name))
                        break
                    case 'Intervention':
                        setDefaultQuery('Select * from ' + (experiment.data_mappings?.delta_intervention_table_name ?? experiment.data_mappings?.user_created_intervention_table_name))
                        break
                    case 'Recommendation':
                        setDefaultQuery('Select * from ' + (experiment.data_mappings?.delta_recommendation_table_name ?? experiment.data_mappings?.user_created_recommendation_table_name))
                        break
                    case 'recurring':
                        setDefaultQuery('Select * from ' + experiment.data_mappings?.recurring_table_name)
                        break
                    default:
                        setDefaultQuery('Select * from ' + (experiment.data_mappings?.delta_measurement_table_name ?? experiment.data_mappings?.user_created_measurement_table_name))
                        break
                }
            } else {
                setDefaultQuery('Select * from ' + (experiment?.data_mappings?.delta_design_table_name ?? experiment?.data_mappings?.user_created_table_name))
            }
        }
    }, [experiment])

    const saveFeatures = () => {
        savefeaturesModalRef.current?.showModal()
    }

    const sendSqlFilter = (data: any) => {
        trackEvent('wizard_sql_executed', { query_length: query?.length })
        applySQL({
            sql_query: query,
        })
    }

    const clearFilter = () => {
        setQuery(defaultQuery)
        setCurrentQueryExecuted(false)
    }
    const CHUNK_ROW_COUNT = 10000 // Number of rows per chunk

    const uploadAdditionalCsv = (chunk: Blob, chunkIndex: number, totalChunks: number, fileName: string) => {
        const formData = new FormData()
        const literal: any = params?.experiment
        formData.append('file', chunk, fileName)
        formData.append('chunk_index', chunkIndex.toString())
        formData.append('total_chunks', totalChunks.toString())
        formData.append('filename', fileName)
        formData.append('upload_type', type)

        return new Promise((resolve, reject) => {
            console.log('Uploading chunk', chunkIndex + 1, 'of', totalChunks)
            experimentApi.uploadadditionfile(
                {
                    formData,
                    literal,
                    progress: (progress: any) => {
                        //setProgress(Math.round(progress.progress * 100))
                    },
                },
                (data: any) => {
                    console.log('upload success data', data)
                    resolve(data)
                },
                (error: any) => {
                    console.error('Error uploading chunk', error)
                    toast.error(error?.message || t('Error uploading chunk'))
                    reject(new Error(typeof error === 'string' ? error : error?.message || t('Unknown error')))
                },
            )
        })
    }

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        if (file && !file.name.toLowerCase().endsWith('.csv')) {
            e.target.value = ''
            toast.error(t('Only CSV files are accepted'))
            return
        }

        //setProgress(0)
        showLoader()

        try {
            // 1. Read file as text
            const text = await file.text()
            const lines = text.split(/\r?\n/)

            // 2. Separate header and data rows
            const header = lines[0]
            const dataRows = lines.slice(1)
            const totalChunks = Math.ceil(dataRows.length / CHUNK_ROW_COUNT)

            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_ROW_COUNT
                const end = start + CHUNK_ROW_COUNT
                const chunkRows = [header, ...dataRows.slice(start, end)]
                const chunkText = chunkRows.join('\n')
                const chunkBlob = new Blob([chunkText], { type: file.type })

                console.log(`Uploading chunk ${i + 1}/${totalChunks}`)
                await uploadAdditionalCsv(chunkBlob, i, totalChunks, file.name)
                //setProgress(Math.round(((i + 1) / totalChunks) * 100))
            }

            const payload = {
                upload_type: type,
                file_name: file.name,
                total_chunk: totalChunks,
            }
            experimentApi.getadditionaltabledata(
                {
                    literal: params.experiment,
                    ...payload,
                },
                (data: any) => {
                    experimentApi.getExperimentDetails(
                        { literal: params.experiment + '?use_orm=true' },
                        (resp: any) => {
                            hideLoader()
                            const tableName = type == 'design' ? resp?.data?.data_mappings?.additional_design_table_name : resp?.data?.data_mappings?.additional_measurement_table_name
                            if (tableName) {
                                setAdditionalFileUploaded(true)
                            } else {
                                setAdditionalFileUploaded(false)
                            }
                            onadditionalCsvUploaded()
                        },
                        (error: any) => {
                            hideLoader()
                            onadditionalCsvUploaded()
                        },
                    )
                },
                (error: any) => {
                    hideLoader()
                    if (error?.message) {
                        toast.error(error?.message)
                    } else {
                        toast.error(error)
                    }
                },
            )
        } catch (error) {
            hideLoader()
            console.error('There was an error uploading the file.', error)
            // Optionally show error to user
        }
    }

    useEffect(() => {
        let QueryToFill = ''
        if (type == 'design') {
            QueryToFill = experiment?.experiment_metadatas?.design_table_filters?.sql_query
        } else {
            switch (dataType) {
                case 'Measurement':
                    QueryToFill = experiment?.experiment_metadatas?.measurement_table_filters?.sql_query
                    break
                case 'Intervention':
                    QueryToFill = experiment?.experiment_metadatas?.intervention_table_filters?.sql_query
                    break
                case 'Recommendation':
                    QueryToFill = experiment?.experiment_metadatas?.recommendation_table_filters?.sql_query
                    break
                default:
                    QueryToFill = experiment?.experiment_metadatas?.measurement_table_filters?.sql_query
                    break
            }
        }

        if (selectedDataTable) {
            QueryToFill = 'Select * from ' + selectedDataTable
        }

        if (QueryToFill) {
            setQuery(QueryToFill)
            setSavedQuery((prev) => [...prev, QueryToFill])
            applySQL({
                sql_query: QueryToFill,
            })
        } else if (type == 'design') {
            setQuery('Select * from ' + (experiment?.data_mappings?.delta_design_table_name ?? experiment?.data_mappings?.user_created_table_name))
        } else {
            switch (dataType) {
                case 'Measurement':
                    setQuery('Select * from ' + (experiment?.data_mappings?.delta_measurement_table_name ?? experiment?.data_mappings?.user_created_measurement_table_name))
                    break
                case 'Intervention':
                    setQuery('Select * from ' + (experiment?.data_mappings?.delta_intervention_table_name ?? experiment?.data_mappings?.user_created_intervention_table_name))
                    break
                case 'Recommendation':
                    setQuery('Select * from ' + (experiment?.data_mappings?.delta_recommendation_table_name ?? experiment?.data_mappings?.user_created_recommendation_table_name))
                    break
                default:
                    setQuery('Select * from ' + (experiment?.data_mappings?.delta_measurement_table_name ?? experiment?.data_mappings?.user_created_measurement_table_name))
                    break
            }
        }
        const checkTableName = type == 'design' ? experiment?.data_mappings?.additional_design_table_name : experiment?.data_mappings?.additional_measurement_table_name
        if (checkTableName) {
            setAdditionalFileUploaded(true)
        } else {
            setAdditionalFileUploaded(false)
        }
    }, [])

    useEffect(() => {
        if (_ref?.current) {
            _ref.current.saveFeatures = saveFeatures
        }
    }, [_ref])

    useEffect(() => {
        if (query && query !== defaultQuery && !savedQuery.includes(query)) {
            setCurrentQueryExecuted(true)
        } else {
            setCurrentQueryExecuted(false)
        }
    }, [currentQueryExecuted, savedQuery])

    return (
        <div data-testid="sql-component" ref={_ref}>
            <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2" data-testid="sql-editor-container">
                    <div className="flex justify-between items-center">
                        <Text size="md" className="my-2" data-testid="sql-input-label">
                            {t('Input SQL Query')}
                        </Text>
                        {experiment?.data_mappings?.additional_design_table_name && (
                            <p className="text-[#0F0F0F] !text-[0.75rem] !mb-0 leading-6 ">
                                {t('Table name:')} {experiment?.data_mappings?.additional_design_table_name}
                            </p>
                        )}
                        {!additionalFileUploaded && (
                            <Button theme="secondary" styleType="outline" size="xs" className="relative my-2 cursor-pointer">
                                <span>{t('Attach CSV File')}</span>
                                <input type="file" onChange={handleFileChange} data-testid="file-input" className="absolute left-0 top-0 w-full h-full opacity-0 cursor-pointer" accept=".csv" />
                            </Button>
                        )}
                        {additionalFileUploaded && (
                            <Button
                                onClick={() => {
                                    dialog.confirm({
                                        type: 'delete',
                                        title: t('Upload CSV Again'),
                                        message: t('Are you sure do you want to upload file again?'),
                                        warning: t('Your current changes will be canceled'),
                                        confirmButtonLabel: t('Yes Proceed'),
                                        onConfirm: () => {
                                            setAdditionalFileUploaded(false)
                                        },
                                        onCancel: () => {
                                            console.log('No')
                                        },
                                    })
                                }}
                                theme="secondary"
                                styleType="outline"
                                size="xs"
                                className="relative my-2 cursor-pointer text-[0.733rem]"
                            >
                                {t('Again Upload CSV')}
                            </Button>
                        )}
                    </div>

                    <div className="relative h-[14rem] border border-[#e6e6e6] rounded-md p-2" data-testid="sql-editor-wrapper">
                        <AceEditor
                            data-testid="sql-editor"
                            mode="mysql"
                            theme="xcode"
                            onChange={(newValue) => {
                                setQuery(newValue)
                                setCurrentQueryExecuted(false)
                            }}
                            editorProps={{ $blockScrolling: true }}
                            className="!w-full !h-full"
                            value={query}
                            setOptions={{ useWorker: false }}
                        />
                    </div>
                </div>
                <div data-testid="accordion-container">
                    <ZoneDesignTablesAccordians />
                </div>
            </div>

            <div className="flex items-center justify-between my-4" data-testid="sql-buttons">
                <div>
                    <Button theme="secondary" styleType="outline" size="xs" onClick={saveFeatures} disabled={!currentQueryExecuted} data-testid="save-features-button">
                        <Icon name="save" />
                        {t('Save Features')}
                    </Button>
                </div>
                <div className="flex items-center gap-3">
                    <Button onClick={clearFilter} theme="secondary" styleType="outline" size="xs" data-testid="clear-button">
                        {t('Clear')}
                    </Button>
                    <Button onClick={sendSqlFilter} theme="secondary" size="xs" className="text-white" data-testid="run-button">
                        <Icon name="code-run" size={1} />
                        {t('Run')}
                    </Button>
                </div>
            </div>

            <SaveFeaturesModal
                _ref={savefeaturesModalRef}
                query={query}
                onSaved={(query: string) => {
                    setSavedQuery((prev) => [...prev, query])
                }}
                data-testid="save-features-modal"
            />
        </div>
    )
}

export default Sql
